import { useEffect, useRef } from "react";
import { ImagePlus, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { uploadImage } from "@/components/ImageUploader";

export const TRACKS = [
  { id: "woman", label: "I'm applying as a woman" },
  { id: "man", label: "I'm applying as a man" },
];
export const NET_WORTH = { "25k": "$25,000+", "75k": "$75,000+", "100k": "$100,000+", "150k": "$150,000+", "200k": "$200,000+", "250k": "$250,000+", "300k": "$300,000+", "450k": "$450,000+", "500k": "$500,000+", "550k": "$550,000+", "600k": "$600,000+", "650k": "$650,000+", "700k": "$700,000+", "750k": "$750,000+", "800k": "$800,000+", "850k": "$850,000+", "900k": "$900,000+", "950k": "$950,000+", "1m_plus": "$1m+", other: "Other" };
const LEGACY_NET_WORTH = { "1m": "€1M+", "5m": "€5M+", "25m": "€25M+" };
export const netWorthLabel = (band, other) => (band === "other" ? other || "Other" : NET_WORTH[band] || LEGACY_NET_WORTH[band] || "");
const TYPES = ["image/jpeg", "image/png", "image/webp"];
export const MAX_PHOTOS = 6;

export async function uploadApplicationPhotos(photos) {
  const paths = [];
  for (const file of photos) paths.push(await uploadImage(file, "application"));
  const { data } = await api.post("/application/media", { photos: paths });
  return data;
}

export const TrackPicker = ({ value, onChange, error }) => (
  <fieldset>
    <legend className="field-label">Application track</legend>
    <div className="grid gap-3 sm:grid-cols-2">
      {TRACKS.map((t) => (
        <button key={t.id} type="button" aria-pressed={value === t.id} data-testid={`track-${t.id}`} onClick={() => onChange(t.id)}
          className={cn("min-h-[56px] rounded-[10px] border px-5 text-left transition-colors", value === t.id ? "border-primary bg-primary/5 text-primary" : "border-foreground/30 hover:border-primary/60")}>
          {t.label}
        </button>
      ))}
    </div>
    {error && <p className="mt-2 text-sm text-destructive" role="alert">{error}</p>}
  </fieldset>
);


export const LocalPhotoPicker = ({ files, onChange, max = MAX_PHOTOS, error }) => {
  const input = useRef(null);
  const urls = useRef(new Map());
  const urlFor = (f) => {
    if (!urls.current.has(f)) urls.current.set(f, URL.createObjectURL(f));
    return urls.current.get(f);
  };
  useEffect(() => () => urls.current.forEach((u) => URL.revokeObjectURL(u)), []);

  const add = (list) => {
    const ok = Array.from(list || []).filter((f) => {
      if (!TYPES.includes(f.type)) { toast.error(`${f.name} must be JPG, PNG or WEBP`); return false; }
      if (f.size > 8 * 1024 * 1024) { toast.error(`${f.name} is larger than 8MB`); return false; }
      return true;
    });
    onChange([...files, ...ok].slice(0, max));
    if (input.current) input.current.value = "";
  };

  return (
    <fieldset>
      <legend className="field-label">Photos <span className="text-muted-foreground">(optional, up to {max})</span></legend>
      <div className="grid grid-cols-3 gap-3" data-testid="apply-photos">
        {files.map((f, i) => (
          <div key={urlFor(f)} className="relative aspect-[3/4] overflow-hidden rounded-lg border border-border">
            <img src={urlFor(f)} alt={`Your photo ${i + 1}`} className="img-cover" />
            <button type="button" aria-label={`Remove photo ${i + 1}`} data-testid={`apply-photo-remove-${i}`} onClick={() => onChange(files.filter((x) => x !== f))}
              className="absolute right-2 top-2 flex h-9 w-9 items-center justify-center rounded-full bg-background/80 hover:bg-background"><X className="h-4 w-4" /></button>
          </div>
        ))}
        {files.length < max && (
          <button type="button" onClick={() => input.current?.click()} data-testid="apply-photo-add"
            className="flex aspect-[3/4] flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-foreground/30 text-sm text-muted-foreground transition-colors hover:border-primary hover:text-primary">
            <ImagePlus className="h-5 w-5" /> Add photo
          </button>
        )}
      </div>
      <input ref={input} type="file" accept={TYPES.join(",")} multiple hidden onChange={(e) => add(e.target.files)} data-testid="apply-photo-input" />
      <p className="mt-2 text-xs text-muted-foreground">Photos are private to the club's review team and are never shown to members.</p>
      {error && <p className="mt-2 text-sm text-destructive" role="alert">{error}</p>}
    </fieldset>
  );
};

export const MenFields = ({ f, set, errors }) => (
  <>
    <div>
      <label htmlFor="net-worth" className="field-label">Net worth</label>
      <select id="net-worth" className="field" value={f.net_worth_band} onChange={(e) => set("net_worth_band")(e.target.value)} data-testid="apply-networth-select" aria-invalid={!!errors.net_worth_band}>
        <option value="">Choose a band</option>
        {Object.entries(NET_WORTH).filter(([k]) => k !== "other").map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </select>
      {errors.net_worth_band && <p className="mt-2 text-sm text-destructive" role="alert">{errors.net_worth_band}</p>}
      {f.net_worth_band === "other" && (
        <>
          <input className="field mt-3" maxLength={40} placeholder="e.g. $1m, $500k" value={f.net_worth_other} onChange={(e) => set("net_worth_other")(e.target.value)}
            aria-label="Your net worth" aria-invalid={!!errors.net_worth_other} data-testid="apply-networth-other-input" />
          {errors.net_worth_other && <p className="mt-2 text-sm text-destructive" role="alert">{errors.net_worth_other}</p>}
        </>
      )}
    </div>
    <div>
      <label htmlFor="occupation" className="field-label">Occupation</label>
      <input id="occupation" className="field" maxLength={100} placeholder="e.g. Founder, Investor" value={f.occupation} onChange={(e) => set("occupation")(e.target.value)} data-testid="apply-occupation-input" aria-invalid={!!errors.occupation} />
      {errors.occupation && <p className="mt-2 text-sm text-destructive" role="alert">{errors.occupation}</p>}
    </div>
    <div>
      <label htmlFor="company" className="field-label">Company <span className="text-muted-foreground">(optional)</span></label>
      <input id="company" className="field" maxLength={100} value={f.company} onChange={(e) => set("company")(e.target.value)} data-testid="apply-company-input" />
    </div>
    <div>
      <label htmlFor="linkedin" className="field-label">LinkedIn <span className="text-muted-foreground">(optional)</span></label>
      <input id="linkedin" type="url" className="field" maxLength={200} placeholder="https://www.linkedin.com/in/…" value={f.linkedin} onChange={(e) => set("linkedin")(e.target.value)} data-testid="apply-linkedin-input" aria-invalid={!!errors.linkedin} />
      {errors.linkedin && <p className="mt-2 text-sm text-destructive" role="alert">{errors.linkedin}</p>}
    </div>
    <div>
      <label htmlFor="reference" className="field-label">Reference <span className="text-muted-foreground">(optional, e.g. an existing member)</span></label>
      <input id="reference" className="field" maxLength={300} value={f.reference} onChange={(e) => set("reference")(e.target.value)} data-testid="apply-reference-input" />
    </div>
  </>
);
